// Per-player hot/cold status split by batting and pitching signals.
// hot  = 2+ signals in that direction, 0 opposing, AND all absolute guardrails pass
// cold = 2+ signals in that direction, 0 opposing, AND all absolute guardrails pass
// null = mixed, insufficient data, or guardrail veto

export type PlayerHotCold = {
  batting: 'hot' | 'cold' | null;
  pitching: 'hot' | 'cold' | null;
  score: number;        // sum of hot signal magnitudes — used to rank players on home screen
  topSignals: string[]; // top 2 label strings driving the hot status, highest magnitude first
};

const nr = (val: any): number | null => {
  if (val == null || val === '') return null;
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  return n > 1 ? n / 100 : n;
};

const countStreak = (arr: any[], ok: (s: any) => boolean): number => {
  let n = 0;
  for (let i = arr.length - 1; i >= 0; i--) { if (ok(arr[i])) n++; else break; }
  return n;
};

const resolve = (hot: number, cold: number): 'hot' | 'cold' | null =>
  hot >= 2 && cold === 0 ? 'hot' : cold >= 2 && hot === 0 ? 'cold' : null;

const fmt3 = (n: number) => n.toFixed(3).replace(/^0/, '');
const fmtPct = (n: number) => `${Math.round(n * 100)}%`;

export function computePlayerHotCold(
  games: any[],
  playerSeasonStats: Map<string, any>
): Map<string, PlayerHotCold> {
  const result = new Map<string, PlayerHotCold>();
  if (games.length < 2) return result;

  const sorted = [...games].sort(
    (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime()
  );

  // Team avg S% for command streak threshold
  let teamSPctSum = 0, teamSPctCount = 0;
  playerSeasonStats.forEach(s => {
    if (s.sPct > 0) { teamSPctSum += s.sPct; teamSPctCount++; }
  });
  const teamAvgSPct = teamSPctCount > 0 ? teamSPctSum / teamSPctCount : null;

  playerSeasonStats.forEach((season, pid) => {
    const battingGames = sorted
      .map(g => g.stats?.[pid])
      .filter((s): s is any => s != null && (s.pa > 0 || s.ab > 0));

    const pitchingGames = sorted
      .map(g => g.stats?.[pid])
      .filter((s): s is any => s != null && ((s.ip || 0) > 0 || (s.pitches || 0) > 0));

    let bHot = 0, bCold = 0;
    let pHot = 0, pCold = 0;

    // Track labelled signals for home screen summary
    type Sig = { label: string; magnitude: number; direction: 'hot' | 'cold'; category: 'batting' | 'pitching' };
    const sigs: Sig[] = [];

    const addSig = (label: string, magnitude: number, direction: 'hot' | 'cold', category: 'batting' | 'pitching') => {
      sigs.push({ label, magnitude, direction, category });
      if (category === 'batting') direction === 'hot' ? bHot++ : bCold++;
      else direction === 'hot' ? pHot++ : pCold++;
    };

    // ── Batting: pre-compute last-3 absolutes for guardrail checks ────────
    const bLast3  = battingGames.slice(-3);
    const bL3ab   = bLast3.reduce((a: number, s: any) => a + (s.ab || 0), 0);
    const bL3so   = bLast3.reduce((a: number, s: any) => a + (s.so || 0), 0);
    const bL3kRate = bL3ab > 0 ? bL3so / bL3ab : 0;
    const bL3pa   = bLast3.reduce((a: number, s: any) => a + (s.pa || 1), 0);
    const bL3ops  = bL3pa > 0
      ? bLast3.reduce((a: number, s: any) => a + (s.ops || 0) * (s.pa || 1), 0) / bL3pa
      : 0;
    const bL3hits = bLast3.reduce((a: number, s: any) => a + (s.h || 0), 0);

    // ── Batting streaks ──────────────────────────────────────────────────
    if (battingGames.length >= 2) {
      const hitStr = countStreak(battingGames, s => (s.h || 0) >= 1);
      const obStr  = countStreak(battingGames, s => ((s.h || 0) + (s.bb || 0)) >= 1);
      const rbiStr = countStreak(battingGames, s => (s.rbi || 0) >= 1);

      if (hitStr >= 2) addSig(`${hitStr}-game hit streak`, hitStr * 10, 'hot', 'batting');
      if (obStr >= 2 && obStr > hitStr && bL3hits >= 1)
        addSig(`${obStr}-game on-base streak`, obStr * 9, 'hot', 'batting');
      if (rbiStr >= 2) addSig(`${rbiStr}-game RBI streak`, rbiStr * 8, 'hot', 'batting');

      let coldN = 0, coldAB = 0;
      for (let i = battingGames.length - 1; i >= 0; i--) {
        const s = battingGames[i];
        if ((s.h || 0) === 0 && (s.ab || 0) >= 2) { coldN++; coldAB += (s.ab || 0); } else break;
      }
      if (coldN >= 2) addSig(`0-for-${coldAB} last ${coldN} games`, coldN * 8, 'cold', 'batting');
    }

    // ── Batting rolling trends ───────────────────────────────────────────
    if (battingGames.length >= 3) {
      if (season.ops > 0) {
        const delta = bL3ops - season.ops;
        if (Math.abs(delta / season.ops) >= 0.15) {
          const dir = delta > 0 ? 'hot' : 'cold';
          addSig(`OPS ${delta > 0 ? '↑' : '↓'} last 3 (${fmt3(bL3ops)} vs ${fmt3(season.ops)} season)`,
            Math.abs(delta / season.ops) * 100, dir, 'batting');
        }
      }

      if (season.ab > 0) {
        const seasonK = (season.so || 0) / season.ab;
        if (bL3ab > 0 && seasonK > 0) {
          const delta = bL3kRate - seasonK;
          if (Math.abs(delta / seasonK) >= 0.15 && (delta < 0 || bL3kRate >= 0.34)) {
            const dir = delta < 0 ? 'hot' : 'cold';
            addSig(`K rate ${delta < 0 ? '↓' : '↑'} last 3 (${fmtPct(bL3kRate)} vs ${fmtPct(seasonK)} season)`,
              Math.abs(delta / seasonK) * 80, dir, 'batting');
          }
        }
      }
    }

    // ── Batting guardrails ───────────────────────────────────────────────
    let batting = resolve(bHot, bCold);
    if (batting === 'hot') {
      if (bL3kRate >= 0.34) batting = null;
      if (bL3ops > 0 && bL3ops < 0.600) batting = null;
    }
    if (batting === 'cold') {
      if (bL3ops >= 0.800) batting = null;
    }

    // ── Pitching: pre-compute last-3 absolutes ────────────────────────────
    const pLast3    = pitchingGames.slice(-3);
    const pL3ip     = pLast3.reduce((a: number, s: any) => a + (s.ip || 0), 0);
    const pL3whip   = pL3ip > 0
      ? pLast3.reduce((a: number, s: any) => a + (s.whip || 0) * (s.ip || 0), 0) / pL3ip
      : 0;
    const pL3pitches = pLast3.reduce((a: number, s: any) => a + (parseInt(s.pitches) || 0), 0);
    const pL3sp     = pL3pitches > 0
      ? pLast3.reduce((a: number, s: any) => {
          const sp = nr(s.sPct);
          return sp != null ? a + sp * (parseInt(s.pitches) || 1) : a;
        }, 0) / pL3pitches
      : 0;

    // ── Pitching streaks ─────────────────────────────────────────────────
    if (pitchingGames.length >= 2) {
      const scoreless = countStreak(pitchingGames, s => s.era === 0 && (s.ip || 0) > 0);
      if (scoreless >= 2) addSig(`${scoreless} scoreless outings`, scoreless * 12, 'hot', 'pitching');

      if (teamAvgSPct != null) {
        const pct = Math.round(teamAvgSPct * 100);
        const cmdStr = countStreak(pitchingGames, s => {
          const sp = nr(s.sPct);
          return sp != null && sp >= teamAvgSPct && sp >= 0.55;
        });
        if (cmdStr >= 2) addSig(`Locating pitches: ${cmdStr} outings ${pct}%+ strikes`, cmdStr * 9, 'hot', 'pitching');
      }
    }

    // ── Pitching rolling trends ──────────────────────────────────────────
    if (pitchingGames.length >= 3) {
      if (season.whip > 0 && pL3ip > 0) {
        const delta = pL3whip - season.whip;
        if (Math.abs(delta / season.whip) >= 0.15) {
          const dir = delta < 0 ? 'hot' : 'cold';
          addSig(`WHIP ${delta < 0 ? '↓' : '↑'} last 3 (${pL3whip.toFixed(2)} vs ${season.whip.toFixed(2)} season)`,
            Math.abs(delta / season.whip) * 90, dir, 'pitching');
        }
      }

      if (season.sPct > 0 && pL3pitches > 0) {
        const delta = pL3sp - season.sPct;
        if (Math.abs(delta / season.sPct) >= 0.15) {
          const dir = delta > 0 ? 'hot' : 'cold';
          addSig(`S% ${delta > 0 ? '↑' : '↓'} last 3 (${fmtPct(pL3sp)} vs ${fmtPct(season.sPct)} season)`,
            Math.abs(delta / season.sPct) * 80, dir, 'pitching');
        }
      }
    }

    // ── Pitching guardrails ──────────────────────────────────────────────
    let pitching = resolve(pHot, pCold);
    if (pitching === 'hot') {
      if (pL3ip > 0 && pL3whip > 2.0) pitching = null;
      if (pL3pitches > 0 && pL3sp < 0.55) pitching = null;
    }
    if (pitching === 'cold') {
      if (pL3ip > 0 && pL3whip <= 1.2) pitching = null;
    }

    if (!batting && !pitching) return;

    // Build topSignals: top 2 labels from signals that match the winning directions
    const winningDir = (batting === 'hot' || pitching === 'hot') ? 'hot' : 'cold';
    const relevantSigs = sigs
      .filter(s => s.direction === winningDir &&
        ((s.category === 'batting' && batting === winningDir) ||
         (s.category === 'pitching' && pitching === winningDir)))
      .sort((a, b) => b.magnitude - a.magnitude);

    const score = relevantSigs.reduce((a, s) => a + s.magnitude, 0);
    const topSignals = relevantSigs.slice(0, 2).map(s => s.label);

    result.set(pid, { batting, pitching, score, topSignals });
  });

  return result;
}
